import clsx from 'clsx';
import * as React from 'react';

export type ActionableContainerProps = React.ComponentPropsWithoutRef<'div'> & {
    /**
     * The orientation of the container.
     * @default 'vertical'
     */
    orientation?: 'horizontal' | 'vertical';

    /**
     * Prevent wrapping for small
     */
    noWrap?: boolean;
};

/**
 * Used to display a content with actions.
 * @example
 * // Vertical
 * <ActionableContainer>
 *   <Heading>Title</Heading>
 *   <Button label="Action" />
 * </ActionableContainer>
 *
 * // Horizontal
 * <ActionableContainer orientation="horizontal">
 *   <Heading>Title</Heading>
 *   <Button label="Action" />
 * </ActionableContainer>
 */
export function ActionableContainer({
    ref,
    ...props
}: ActionableContainerProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const { orientation = 'vertical', noWrap, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx(
                'group/actionable-container flex flex-col gap-x-4 gap-y-2',
                {
                    horizontal: clsx(
                        'grid-cols-[minmax(0,1fr)_auto]',
                        // For switch, always render horizontal
                        String.raw`has-[>.peer\/switch]:grid has-[>.peer\/switch]:items-center`,
                        // If `noWrap` is set, always render horizontal
                        noWrap ? 'grid items-center' : 'items-start sm:grid sm:items-center'
                    ),
                    vertical: 'min-w-0 flex-1',
                }[orientation],
                props.className
            )}
        />
    );
}
